import { track } from '@amplitude/analytics-browser'
import Bugsnag from '@bugsnag/js'
import isEmpty from 'lodash.isempty'
import uniqBy from 'lodash.uniqby'
import { useEffect, useState } from 'react'
import { useScrollDirection } from 'react-use-scroll-direction'
import styled from 'styled-components'

import { fetchPostsPreview, fetchGridPreviewByAccount } from 'api'
import type {
  AccountSelectorProps,
  UpcomingPostCardProps,
  ConversationProps,
  PostActionProps,
} from 'Components'
import { Navbar, LoadingPlaceholder, EmptyUpcomingPosts } from 'Components'
import Dropdown from 'Components/Common/Dropdown'
import PoweredBySked from 'Components/Common/PoweredBySked'
import { Title, FlexAlignCenter, Desc } from 'Components/CommonStyleComponents'
import { getLastStatusChangeMessage } from 'Components/conversation/utils'
import MobilePreviewGrid from 'Components/Mobile/MobilePreviewGrid'
import MobileUpcomingPostCard from 'Components/Mobile/MobileUpcomingPostCard'
import {
  datePlaceholderForDraftPost,
  normalizeGridPreviewAllPosts,
  normalizeSkedPreviewPost,
} from 'normalizer'
import { ensureError } from 'utils/ensureError'
import getInstagramAccountOption from 'utils/getInstagramAccountOption'

const Container = styled.div`
  margin: 10px;
`

const MobileUpcomingPostCardWrap = styled.div<{ isVisible: boolean }>`
  display: ${(props) => (props.isVisible ? 'block' : 'none')};
`

const NavbarStyled = styled(Navbar)`
  &&& {
    margin: -10px;
    padding: 0 10px;
    height: 65px;

    > div img {
      width: 32px;
      margin-right: 14px;
    }
  }
`

const UpcomingPostTitle = styled(Title)`
  margin: 26px auto 16px;
  text-align: center;
`

const PostGridPreviewTab = styled(FlexAlignCenter)<{ isVisible: boolean }>`
  margin: 0 -10px 20px;
  justify-content: space-around;
  border-bottom: 1px solid #c4c4c4;
  position: sticky;
  top: 0;
  z-index: 3;
  background: #ffffff;
  padding: 10px;
  transition: all 0.1s ease-in;
  ${(props) =>
    props.isVisible
      ? 'visibility: visible; opacity: 1'
      : 'visibility: hidden; opacity: 0'};

  > .tab {
    flex: 1;
    text-align: center;
    font-size: 16px;
    font-weight: 700;
    margin-bottom: 5px;
    cursor: pointer;

    &.active {
      color: #4f31ff;
      opacity: 1;
    }

    &.disabled {
      pointer-events: none;
    }
  }

  /* Reset native button chrome so the new <button class="tab"> elements
     pick up the same styling the original <div class="tab"> got. */
  > button.tab {
    background: transparent;
    border: none;
    padding: 0;
    font: inherit;
    color: inherit;
  }
`

const PostOrGridViewTabActive = styled.div<{ isAlignRight: boolean }>`
  height: 5px;
  border-radius: 5px;
  position: absolute;
  width: 50%;
  background: #4f31ff;
  bottom: -3px;
  left: 0;

  ${(props) =>
    props.isAlignRight &&
    `
    left: auto;
    right: 0;
  `}
`

const UpcomingPostCardWrap = styled.div`
  .date {
    font-size: 16px;
    font-weight: 700;
    margin: 28px auto 18px 12px;
    color: #454545;

    &.hidden {
      display: none;
    }
  }
`

export const PendingCountWrap = styled.span<{ isDraftPost: boolean }>`
  color: #4f31ff;
  margin-left: ${(props) => (props.isDraftPost ? '0px' : '8px')};

  .bullet {
    color: #454545;
  }
`

const PoweredBySkedStyled = styled(PoweredBySked)`
  &&& {
    display: flex;
    place-content: center;
    margin: 20px 0;
  }
`

const InstagramOnlyLabel = styled(Desc)`
  display: flex;
  justify-content: center;
  margin: 18px auto;
  font-weight: 500;
`

const MobilePreviewGridWrap = styled.div`
  display: grid;
  place-content: center;
  margin: 18px auto;
`

const CalendarNotice = styled.div`
  font-size: 16px;
  font-weight: 700;
  padding: 12px 14px;
  background: #f6f5ff;
  border: 1px solid #4f31ff;
  border-radius: 5px;
  margin-bottom: -14px;
`

interface AppMobileProps {
  isLoading: boolean
  setting: {
    logoUrl: string
    companyName: string
  }
  accountSelectorProps: AccountSelectorProps
  upcomingPosts: {
    id: string
    date: string
    isVisible: boolean
    posts: (UpcomingPostCardProps & {
      isVisible?: boolean
      postIndex: number
    })[]
  }[]
  updatePostState: (
    reviewKey: string,
    postId: string,
    finalState: string
  ) => void
  accountsRaw: any
  conversationProps: ConversationProps
  postActionProps: PostActionProps
  reviewKey: string
  postId: string
  fetchComment?: (reviewKey: string, postId: string) => void
  isReadOnly: boolean
}

type PostOrGridView = 'postView' | 'gridView'

const AppMobile = (props: AppMobileProps) => {
  const {
    isLoading,
    setting: { logoUrl, companyName },
    accountSelectorProps,
    upcomingPosts,
    accountsRaw, // to build skedPreviewPosts
    conversationProps,
    postActionProps,
    reviewKey,
    isReadOnly,
    updatePostState,
  } = props

  const [postOrGridView, setPostOrGridView] =
    useState<PostOrGridView>('postView')

  const [skedPreviewsPosts, setSkedPreviewsPosts] = useState<any[]>([])

  const [gridData, setGridData] = useState([])
  const [gridDataCached, setGridDataCached] = useState([])

  const [isSkedPreviewLoading, setIsSkedPreviewLoading] = useState(false)
  const [isGridPreviewLoading, setIsGridPreviewLoading] = useState(false)

  const handlePostGridTabClick = (v: PostOrGridView) => () => {
    if (isGridPreviewLoading) {
      alert('please wait...')
    } else {
      setPostOrGridView(v)
    }

    track('ext-planner-tab-click')
  }

  const [showStickyTab, setShowStickyTab] = useState(true)
  const { isScrollingUp, isScrollingDown } = useScrollDirection()
  useEffect(() => {
    if (window.scrollY >= 130) {
      if (isScrollingUp) {
        setShowStickyTab(true)
      }
      if (isScrollingDown) {
        setShowStickyTab(false)
      }
    } else {
      setShowStickyTab(true)
    }
  }, [isScrollingUp, isScrollingDown])

  useEffect(() => {
    const fetchPostsPreviewAsync = async () => {
      setIsSkedPreviewLoading(true)
      try {
        const skedPreviewPostsResponse = await fetchPostsPreview(reviewKey)

        if (!skedPreviewPostsResponse) {
          return
        }

        const skedPreviewsPosts = skedPreviewPostsResponse.map(
          (skedPreviewResponse: any) =>
            normalizeSkedPreviewPost(skedPreviewResponse, accountsRaw)
        )

        setSkedPreviewsPosts(skedPreviewsPosts)
      } catch (error) {
        Bugsnag.notify(ensureError(error))
      } finally {
        setIsSkedPreviewLoading(false)
      }
    }

    if (!isLoading) {
      fetchPostsPreviewAsync()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading])

  const accountDropdownOption = getInstagramAccountOption(
    accountSelectorProps.accounts
  )

  const accountIds = accountDropdownOption.map((acc) => acc.id)

  useEffect(() => {
    if (
      postOrGridView === 'gridView' &&
      isEmpty(gridDataCached) &&
      accountIds.length > 0
    ) {
      const fetchData = async () => {
        setIsGridPreviewLoading(true)
        try {
          if (!accountIds || accountIds.length === 0) return

          const posts = await Promise.all(
            accountIds.map((id) => fetchGridPreviewByAccount(reviewKey, id))
          )

          if (!posts || posts.length === 0) {
            return
          }

          const gridPosts = uniqBy(
            posts.filter((p) => p && p.length).flat(1),
            'id'
          )

          if (gridPosts.length > 0) {
            setGridData(
              normalizeGridPreviewAllPosts(gridPosts).filter((post: any) =>
                post.accounts.some((id: string) => accountIds[0] === id)
              )
            )
            setGridDataCached(normalizeGridPreviewAllPosts(gridPosts))
          }
        } catch (error) {
          Bugsnag.notify(ensureError(error))
        } finally {
          setIsGridPreviewLoading(false)
        }
      }
      fetchData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postOrGridView, gridDataCached, accountIds])

  return (
    <Container>
      <NavbarStyled logo={logoUrl} name={companyName} />
      <UpcomingPostTitle>Upcoming Posts</UpcomingPostTitle>
      <PostGridPreviewTab
        isVisible={showStickyTab}
        data-testid="postGridPreviewTab"
      >
        <button
          type="button"
          className={`tab ${postOrGridView === 'postView' && 'active'}`}
          onClick={handlePostGridTabClick('postView')}
        >
          Post Preview
        </button>
        <button
          type="button"
          className={`tab ${postOrGridView === 'gridView' && 'active'} ${
            isLoading && 'disabled'
          }`}
          onClick={handlePostGridTabClick('gridView')}
          disabled={isLoading}
        >
          Grid Preview
        </button>
        <PostOrGridViewTabActive isAlignRight={postOrGridView === 'gridView'} />
      </PostGridPreviewTab>

      {postOrGridView === 'postView' && isReadOnly && (
        <CalendarNotice data-testid="calendar-view-notification">
          For a better Calendar view, please view this link on a desktop device.
        </CalendarNotice>
      )}

      {!isLoading && !isGridPreviewLoading && postOrGridView === 'gridView' && (
        <>
          <InstagramOnlyLabel>Available for Instagram only</InstagramOnlyLabel>
          <Dropdown
            name="accounts"
            placeholder="Select from options"
            onChange={(selected) => {
              const selectedId = selected.value

              const nextGridData = gridDataCached.filter((post: any) =>
                post.accounts.some((id: string) => selectedId === id)
              )

              if (nextGridData.length > 0) {
                setGridData(nextGridData)
              } else {
                setGridData([])
              }
            }}
            options={accountDropdownOption}
          />
        </>
      )}
      {isLoading ? (
        <LoadingPlaceholder isMobile />
      ) : !isLoading &&
        !isSkedPreviewLoading &&
        isEmpty(upcomingPosts.flatMap((p) => p.posts)) ? (
        <div
          style={{
            marginTop: isReadOnly ? '30px' : 'auto',
          }}
        >
          <EmptyUpcomingPosts />
        </div>
      ) : (
        postOrGridView === 'postView' && (
          <>
            <UpcomingPostCardWrap>
              {upcomingPosts.map(
                ({ date, posts, id: postRowId, isVisible }) => {
                  const renderPendingCount = (
                    posts: UpcomingPostCardProps[]
                  ) => {
                    let pendingCount = 0
                    posts.forEach((post) => {
                      if (post.status === 'pending') {
                        pendingCount += 1
                      }
                    })
                    return pendingCount
                  }
                  return (
                    <div key={postRowId + date}>
                      <div className={`date ${isVisible ? '' : 'hidden'}`}>
                        {date && date === datePlaceholderForDraftPost
                          ? `Drafts`
                          : date}
                        <PendingCountWrap
                          isDraftPost={date === datePlaceholderForDraftPost}
                        >
                          {renderPendingCount(posts) > 0 && !isReadOnly && (
                            <>
                              <span className="bullet">
                                {date !== datePlaceholderForDraftPost &&
                                  '•'}{' '}
                              </span>
                              {renderPendingCount(posts)} pending
                            </>
                          )}
                        </PendingCountWrap>
                      </div>
                      {posts.map((post) => {
                        const { id, status, platform, time } = post
                        const postMessagesEntry =
                          conversationProps.messages?.find(
                            (item) => item.postId === id
                          )
                        const postMessages = postMessagesEntry?.messages || []
                        const lastStatusChangeMessage =
                          getLastStatusChangeMessage(postMessages)
                        const lastStatusChangeComment =
                          lastStatusChangeMessage?.comment
                        const hasPostedComment = Boolean(
                          lastStatusChangeMessage?.isMessageOwner &&
                          lastStatusChangeComment &&
                          lastStatusChangeComment.trim().length > 0
                        )
                        const lastMessageId =
                          postMessages.length > 0
                            ? postMessages[postMessages.length - 1]?.id
                            : undefined
                        const isPostReplied =
                          hasPostedComment ||
                          conversationProps?.isRepliedPosts?.includes(id)

                        return (
                          <MobileUpcomingPostCardWrap
                            isVisible={isVisible}
                            key={status + time + id}
                          >
                            <MobileUpcomingPostCard
                              id={id}
                              status={status}
                              platform={platform}
                              time={time}
                              when={post.when}
                              skedPreviewPost={skedPreviewsPosts.find(
                                (post) => post.id === id
                              )}
                              updatePostState={updatePostState}
                              conversation={{
                                isLoading: conversationProps.isLoading,
                                isSubmitting: conversationProps.isSubmitting,
                                messages: conversationProps.messages,
                                canReply: status === 'requestChange',
                                isReplied: isPostReplied,
                                submitReply: conversationProps.submitReply,
                                reviewKey,
                                postId: id,
                                fetchComment: conversationProps.fetchComment,
                                isConversationsEnabled:
                                  conversationProps.isConversationsEnabled,
                                collaborators: conversationProps.collaborators,
                                isReadOnly,
                                lastMessageId,
                                isStatusChangesVisible:
                                  conversationProps.isStatusChangesVisible,
                                isExternalNotesVisible:
                                  conversationProps.isExternalNotesVisible,
                                isPostActivityVisible:
                                  conversationProps.isPostActivityVisible,
                                username: conversationProps.username,
                              }}
                              postAction={{
                                ...postActionProps,
                                postId: id,
                                postStatus: status,
                                isMobile: true,
                                canReply: status === 'requestChange',
                                isReplied: isPostReplied,
                                lastMessageId,
                              }}
                              isReadOnly={isReadOnly}
                            />
                          </MobileUpcomingPostCardWrap>
                        )
                      })}
                    </div>
                  )
                }
              )}
            </UpcomingPostCardWrap>

            <PoweredBySkedStyled />
          </>
        )
      )}
      {postOrGridView === 'gridView' && (
        <MobilePreviewGridWrap>
          {isGridPreviewLoading ? (
            'fetching... please wait...'
          ) : isEmpty(gridData) ? (
            <>
              <br />
              We couldn&apos;t find any upcoming posts
            </>
          ) : (
            <MobilePreviewGrid posts={gridData} isReadOnly={isReadOnly} />
          )}
        </MobilePreviewGridWrap>
      )}
    </Container>
  )
}

export default AppMobile
